/*
 * Draft 7 screens: Loose ends (a Home card), the Hiring board (HR), and Schedule tools: capacity for the next two weeks,
 * texting a day, and time off. Routes, their order, Best order and route day moves live in the Schedule's Route view
 * (RouteDay.tsx) since 2026-10-29.
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  APPLICANT_STAGE_WORDS,
  NO_FIT_REASONS,
  NO_FIT_WORDS,
  SCREEN_SCORES,
  SCREEN_SCORE_WORDS,
  addDays,
  defaultRoles,
  type ApplicantPatch,
  type ApplicantRow,
  type ApplicantStage,
  type DayTextAnswer,
  type NoFitReason,
  type Settings,
} from "@steward/shared";
import {
  useAddApplicant,
  useAddTimeOff,
  useApplicants,
  useCapacity,
  useClearTimeOff,
  useDayText,
  useHire,
  useMoveApplicant,
  usePeople,
  usePoolText,
  useSettings,
  useTextApplicantLink,
  useTimeOff,
  useUpdateSettings,
} from "../api/hooks";
import { useSession } from "../app/session";
import { Choice, Combobox, InputText } from "../components/fields";
import { shortDate, todayIn } from "../components/format";
import { Modal, useToast } from "../components/overlays";
import { Banner, Button, Card, Chip, Details, EmptyState, Page, Skeleton, StatusLabel, Switch, ago } from "../components/parts";
import { errText } from "../components/work";

// Hiring --------------------------------------------------------------------------------------------------------------

/*
 * Hiring v2 (owner, 2026-11-04: "yes build all"). The board runs New, Talking, Phone screen, Trial day, Offer, Paperwork,
 * Hired; Not now (people to text when we hire again), Not a fit and Archived sit in lists under it. Applicants pick their
 * own phone call time from our text; each card shows their language, where they found us, answers that need a look, and
 * a red clock when nobody has texted a new one within the hour.
 */
const BOARD: ApplicantStage[] = ["new", "talking", "screen", "trial", "offer", "paperwork", "hired"];
const UNDER: ApplicantStage[] = ["not_now", "no_fit", "archived"];
const DAY_LETTERS = ["M", "Tu", "W", "Th", "F", "Sa", "Su"];
const HOUR_MS = 3_600_000;

const callWords = (iso: string, tz: string) =>
  new Date(iso).toLocaleString("en-US", { timeZone: tz, weekday: "short", hour: "numeric", minute: "2-digit" });

export function Hiring() {
  const list = useApplicants();
  const { company, can } = useSession();
  const settings = useSettings(company.id);
  const pool = usePoolText();
  const toast = useToast();
  const [openId, setOpenId] = useState<string | null>(null);
  const [under, setUnder] = useState<ApplicantStage | null>(null);
  const [adding, setAdding] = useState(false);
  const [setup, setSetup] = useState(false);
  if (!list.data) return <Skeleton rows={6} />;
  const all = list.data.applicants;
  const by = (s: ApplicantStage) => all.filter((a) => a.stage === s);
  const open = all.find((a) => a.id === openId) ?? null;
  const careers = `${window.location.origin}/apply/${company.slug}`;
  const textsOff = settings.data ? !settings.data.settings.hiring.texts : false;
  const edit = can("people.update");
  return (
    <Page
      title="Hiring"
      primaryAction={
        edit ? (
          <Button icon="plus" onClick={() => setAdding(true)}>
            Add applicant
          </Button>
        ) : null
      }
      secondaryAction={
        <Button
          type="secondary"
          icon="file"
          onClick={() => void navigator.clipboard.writeText(careers).then(() => toast("Careers page link copied"))}
        >
          Copy careers link
        </Button>
      }
      moreActions={
        can("settings.update") ? (
          <Button type="tertiary" icon="settings" onClick={() => setSetup(true)}>
            Hiring settings
          </Button>
        ) : null
      }
    >
      <div className="stack">
        {textsOff && can("settings.update") ? (
          <Banner
            type="warning"
            action={
              <Button size="small" type="secondary" onClick={() => setSetup(true)}>
                Hiring settings
              </Button>
            }
          >
            Applicant texts are off. Turn them on so new applicants get a text in a minute with a link to pick a phone call time.
          </Banner>
        ) : null}
        {all.length === 0 ? (
          <EmptyState icon="team" title="No applicants yet">
            People who apply on your careers page show here. Share the careers link on Indeed, Facebook and flyers.
          </EmptyState>
        ) : (
          <div className="pipeline hiring">
            {BOARD.map((s) => (
              <section key={s} className="stage" aria-label={APPLICANT_STAGE_WORDS[s]}>
                <div className="stage-head">
                  <b>{APPLICANT_STAGE_WORDS[s]}</b> <span className="muted num">{by(s).length}</span>
                </div>
                <div className="stage-list">
                  {by(s).length === 0 ? <p className="muted stage-empty">No one here</p> : null}
                  {by(s).map((a) => (
                    <ApplicantCard key={a.id} a={a} tz={company.timezone} onOpen={() => setOpenId(a.id)} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
        <div className="chips">
          {UNDER.map((s) => (
            <Chip key={s} pressed={under === s} count={by(s).length} onClick={() => setUnder(under === s ? null : s)}>
              {APPLICANT_STAGE_WORDS[s]}
            </Chip>
          ))}
        </div>
        {under ? (
          <div className="stack">
            {under === "not_now" && edit && by("not_now").some((a) => a.textOk) ? (
              <div>
                <Button
                  type="secondary"
                  size="small"
                  loading={pool.isPending}
                  onClick={() =>
                    pool.mutate(undefined, {
                      onSuccess: (r) => toast(`Texted ${String(r.sent)} that we are hiring again`),
                      onError: (e) => toast(errText(e), { kind: "error" }),
                    })
                  }
                >
                  Text them we are hiring again
                </Button>
              </div>
            ) : null}
            <div className="hiring-archived">
              {by(under).length === 0 ? <p className="muted">No one here</p> : null}
              {by(under).map((a) => (
                <ApplicantCard key={a.id} a={a} tz={company.timezone} onOpen={() => setOpenId(a.id)} />
              ))}
            </div>
          </div>
        ) : null}
      </div>
      {open ? <ApplicantSheet a={open} onClose={() => setOpenId(null)} /> : null}
      {adding ? <AddApplicant onClose={() => setAdding(false)} /> : null}
      {setup && settings.data ? <HiringSettings s={settings.data.settings.hiring} onClose={() => setSetup(false)} /> : null}
    </Page>
  );
}

/** Answers on the form that need a look. Never their language or where they are from. */
function flagsOf(a: ApplicantRow): string[] {
  const out: string[] = [];
  if (a.ride === false) out.push("No ride");
  if (a.workOk === false) out.push("Work papers");
  if (a.days && !a.days.some((d) => d <= 5)) out.push("Weekends only");
  return out;
}

/** One applicant on the board: the whole card opens them. */
function ApplicantCard({ a, tz, onOpen }: { a: ApplicantRow; tz: string; onOpen: () => void }) {
  const waiting = a.stage === "new" && !a.firstTextAt ? Date.now() - Date.parse(a.createdAt) : 0;
  return (
    <button type="button" className="lead applicant" onClick={onOpen}>
      <b>{a.name}</b>
      <span className="muted">{[a.position ?? a.role, a.zip, ago(a.createdAt)].filter(Boolean).join(" · ")}</span>
      <span className="row wrap">
        {a.language === "es" ? <StatusLabel status="informative">Spanish</StatusLabel> : null}
        {(a.src ?? a.channel) ? <StatusLabel status="inactive">{a.src ?? a.channel}</StatusLabel> : null}
        {flagsOf(a).map((f) => (
          <StatusLabel key={f} status="warning">
            {f}
          </StatusLabel>
        ))}
        {a.screenAt && a.stage === "screen" ? (
          <StatusLabel status={a.screenConfirmed ? "success" : "informative"}>
            Call {callWords(a.screenAt, tz)}
            {a.screenConfirmed ? ", said yes" : ""}
          </StatusLabel>
        ) : null}
        {waiting > HOUR_MS ? (
          <StatusLabel status="critical">No text yet, {String(Math.floor(waiting / HOUR_MS))} h</StatusLabel>
        ) : null}
      </span>
    </button>
  );
}

/** An applicant opened: what they said, their call, the scorecard, and moving, hiring or letting them go kindly. */
function ApplicantSheet({ a, onClose }: { a: ApplicantRow; onClose: () => void }) {
  const move = useMoveApplicant();
  const hire = useHire();
  const textLink = useTextApplicantLink();
  const toast = useToast();
  const { can, company } = useSession();
  const [leaving, setLeaving] = useState<"not_now" | "no_fit" | null>(null);
  const edit = can("people.update") && a.stage !== "hired";
  const fail = (e: unknown) => toast(errText(e), { kind: "error" });
  const patch = (body: ApplicantPatch, said: string, close = false) =>
    move.mutate(
      { id: a.id, ...body },
      {
        onSuccess: () => {
          toast(said);
          if (close) onClose();
        },
        onError: fail,
      },
    );
  const footer = edit ? (
    UNDER.includes(a.stage) ? (
      <Button loading={move.isPending} onClick={() => patch({ stage: "talking" }, `${a.name} is back on the board`)}>
        Put back on the board
      </Button>
    ) : (
      <>
        <Button type="secondary" onClick={() => setLeaving("not_now")}>
          Not now
        </Button>
        <Button type="secondary" onClick={() => setLeaving("no_fit")}>
          Not a fit
        </Button>
        {can("people.invite") ? (
          <Button
            loading={hire.isPending}
            onClick={() =>
              hire.mutate(
                { id: a.id, invite: true },
                {
                  onSuccess: () => {
                    toast(`${a.name} is on the team. The invite is on its way.`);
                    onClose();
                  },
                  onError: fail,
                },
              )
            }
          >
            Hire
          </Button>
        ) : null}
      </>
    )
  ) : null;
  const days = a.days?.map((d) => DAY_LETTERS[d - 1]).join(" ");
  return (
    <Modal title={a.name} open onClose={onClose} size="full" footer={footer}>
      <div className="stack">
        <Details
          rows={[
            [
              "Applied",
              `${shortDate(a.createdAt.slice(0, 10))}${a.src ? `, from ${a.src}` : a.source === "careers" || a.source === "website" ? " on the website" : ""}`,
            ],
            ["For", a.position ?? a.role],
            ["Phone", a.phone ? <a href={`tel:${a.phone}`}>{a.phone}</a> : null],
            ["Email", a.email ? <a href={`mailto:${a.email}`}>{a.email}</a> : null],
            ["ZIP", a.zip],
            ["Speaks", a.language === "es" ? "Spanish" : "English"],
            ["Days", days ?? null],
            ["Has a ride", a.ride === null ? null : a.ride ? "Yes" : "No"],
            ["OK to work in the US", a.workOk === null ? null : a.workOk ? "Yes" : "No"],
            ["Can start", a.start],
            ["Sent by", a.referredBy],
            ["Texts", a.textOk ? "Said yes" : "Not asked"],
            [
              "Phone call",
              a.screenAt
                ? `${callWords(a.screenAt, company.timezone)}${a.screenConfirmed ? ", they said yes" : ""}`
                : "Not picked yet",
            ],
            ["Not a fit because", a.reason ? NO_FIT_WORDS[a.reason] : null],
          ]}
        />
        {a.about ? <p className="applicant-about">{a.about}</p> : null}
        {edit && a.bookingLink ? (
          <div className="row wrap">
            {a.phone && a.textOk ? (
              <Button
                type="secondary"
                size="small"
                icon="message"
                loading={textLink.isPending}
                onClick={() =>
                  textLink.mutate(a.id, { onSuccess: () => toast("Texted the link to pick a call time"), onError: fail })
                }
              >
                Text the call link
              </Button>
            ) : null}
            <Button
              type="tertiary"
              size="small"
              icon="file"
              onClick={() => void navigator.clipboard.writeText(a.bookingLink ?? "").then(() => toast("Call time link copied"))}
            >
              Copy call time link
            </Button>
          </div>
        ) : null}
        {edit && !UNDER.includes(a.stage) ? (
          <Choice
            label="Stage"
            value={a.stage}
            onChange={(s) => patch({ stage: s }, `${a.name} moved to ${APPLICANT_STAGE_WORDS[s]}`)}
            options={BOARD.filter((s) => s !== "hired").map((s) => ({ value: s, label: APPLICANT_STAGE_WORDS[s] }))}
          />
        ) : (
          <p className="muted">{APPLICANT_STAGE_WORDS[a.stage]}</p>
        )}
        <Card title="Phone call scorecard">
          <ul className="auto-list">
            {SCREEN_SCORES.map((k) => (
              <li key={k} className="setting-row">
                <span className="what">
                  <b>{SCREEN_SCORE_WORDS[k]}</b>
                </span>
                <span className="row">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Chip
                      key={n}
                      pressed={a.scores[k] === n}
                      onClick={() => (edit ? patch({ scores: { [k]: n } }, "Score saved") : undefined)}
                    >
                      {String(n)}
                    </Chip>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      {leaving ? (
        <LetGo
          a={a}
          stage={leaving}
          busy={move.isPending}
          onClose={() => setLeaving(null)}
          onDone={(body) => {
            patch(body, `${a.name} moved to ${APPLICANT_STAGE_WORDS[leaving]}`, true);
            setLeaving(null);
          }}
        />
      ) : null}
    </Modal>
  );
}

/** Not now or Not a fit: why (for the reports) and the kind text so nobody is left waiting. */
function LetGo({
  a,
  stage,
  busy,
  onClose,
  onDone,
}: {
  a: ApplicantRow;
  stage: "not_now" | "no_fit";
  busy: boolean;
  onClose: () => void;
  onDone: (body: ApplicantPatch) => void;
}) {
  const [reason, setReason] = useState<NoFitReason>("schedule");
  const [tell, setTell] = useState(true);
  const canText = Boolean(a.phone && a.textOk);
  return (
    <Modal
      title={stage === "not_now" ? `Not now for ${a.name}` : `${a.name} is not a fit`}
      open
      onClose={onClose}
      footer={
        <Button
          loading={busy}
          onClick={() => onDone({ stage, ...(stage === "no_fit" ? { reason } : {}), tell: canText && tell })}
        >
          {stage === "not_now" ? "Move to Not now" : "Move to Not a fit"}
        </Button>
      }
    >
      <div className="stack">
        <p className="muted">
          {stage === "not_now"
            ? "They stay on your list. When you need people, one tap texts everyone here that you are hiring again."
            : "They leave the board. The reason helps you see where people drop off."}
        </p>
        {stage === "no_fit" ? (
          <Choice
            label="Why"
            value={reason}
            onChange={setReason}
            options={NO_FIT_REASONS.map((r) => ({ value: r, label: NO_FIT_WORDS[r] }))}
          />
        ) : null}
        {canText ? (
          <div className="setting-row">
            <span className="what">
              <b>Text them a kind note</b>
              <span>So they are not left waiting</span>
            </span>
            <Switch checked={tell} onChange={setTell} label="Text them a kind note" />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/** Type in someone who called or walked in. */
function AddApplicant({ onClose }: { onClose: () => void }) {
  const add = useAddApplicant();
  const toast = useToast();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [language, setLanguage] = useState<"en" | "es">("en");
  const [textOk, setTextOk] = useState(false);
  return (
    <Modal
      title="Add applicant"
      open
      onClose={onClose}
      footer={
        <Button
          loading={add.isPending}
          disabled={!name.trim()}
          onClick={() =>
            add.mutate(
              {
                name: name.trim(),
                ...(phone.trim() ? { phone: phone.trim() } : {}),
                language,
                textOk: textOk && Boolean(phone.trim()),
              },
              {
                onSuccess: () => {
                  toast(`${name.trim()} is on the board`);
                  onClose();
                },
                onError: (e) => toast(errText(e), { kind: "error" }),
              },
            )
          }
        >
          Add
        </Button>
      }
    >
      <div className="stack">
        <InputText label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <InputText label="Phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Choice
          label="Speaks"
          value={language}
          onChange={setLanguage}
          options={[
            { value: "en", label: "English" },
            { value: "es", label: "Spanish" },
          ]}
        />
        <div className="setting-row">
          <span className="what">
            <b>They said yes to texts</b>
            <span>About their application, at this number</span>
          </span>
          <Switch checked={textOk} onChange={setTextOk} label="They said yes to texts" />
        </div>
      </div>
    </Modal>
  );
}

/** Hiring settings: applicant texts, phone call times, roles with trial pay, and the referral bonus. */
function HiringSettings({ s, onClose }: { s: Settings["hiring"]; onClose: () => void }) {
  const save = useUpdateSettings();
  const toast = useToast();
  const { company } = useSession();
  const [texts, setTexts] = useState(s.texts);
  const [times, setTimes] = useState(s.screenTimes.map((t) => ({ ...t })));
  const [minutes, setMinutes] = useState(String(s.screenMinutes));
  const [roles, setRoles] = useState(
    (s.roles.length ? s.roles : defaultRoles(company.trade)).map((r) => ({ ...r, rate: (r.trialRateCents / 100).toFixed(2) })),
  );
  const [bonus, setBonus] = useState((s.referralBonusCents / 100).toFixed(2));
  const [after, setAfter] = useState(String(s.referralAfterDays));
  const [day, setDay] = useState("1");
  const [at, setAt] = useState("10:00");
  const cents = (v: string) => Math.max(0, Math.round(Number(v || "0") * 100)) || 0;
  const submit = () =>
    save.mutate(
      {
        hiring: {
          texts,
          screenTimes: times,
          screenMinutes: Math.min(60, Math.max(5, Number(minutes) || 15)),
          roles: roles
            .filter((r) => r.name.trim())
            .map((r, i) => ({
              id: r.id.trim() || `role${String(i + 1)}`,
              name: r.name.trim(),
              nameEs: r.nameEs.trim(),
              trialRateCents: cents(r.rate),
            })),
          referralBonusCents: cents(bonus),
          referralAfterDays: Math.min(365, Math.max(1, Number(after) || 60)),
        },
      },
      {
        onSuccess: () => {
          toast("Hiring settings saved");
          onClose();
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  const weekday = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const clock = (hhmm: string) => {
    const [h, m] = hhmm.split(":").map(Number) as [number, number];
    return `${String(h % 12 || 12)}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
  };
  return (
    <Modal
      title="Hiring settings"
      open
      onClose={onClose}
      size="large"
      footer={
        <Button loading={save.isPending} onClick={submit}>
          Save
        </Button>
      }
    >
      <div className="stack">
        <div className="setting-row">
          <span className="what">
            <b>Text applicants</b>
            <span>
              A thank you with a link to pick a phone call time, reminders, and a kind note when you move someone to Not now or
              Not a fit. Only people who said yes to texts, 8 AM to 8 PM, in English or Spanish.
            </span>
          </span>
          <Switch checked={texts} onChange={setTexts} label="Text applicants" />
        </div>
        <Card title="Phone call times">
          <p className="muted">Applicants pick from these every week. Booked times close.</p>
          <div className="row wrap">
            {[...times]
              .sort((x, y) => x.day - y.day || x.at.localeCompare(y.at))
              .map((t) => (
                <Chip
                  key={`${String(t.day)}-${t.at}`}
                  pressed
                  onClick={() => setTimes(times.filter((x) => !(x.day === t.day && x.at === t.at)))}
                >
                  {weekday[t.day - 1]} {clock(t.at)} ✕
                </Chip>
              ))}
          </div>
          <div className="row wrap">
            <label className="field">
              <span className="label">Day</span>
              <select className="select" value={day} onChange={(e) => setDay(e.target.value)}>
                {weekday.map((w, i) => (
                  <option key={w} value={String(i + 1)}>
                    {w}
                  </option>
                ))}
              </select>
            </label>
            <InputText label="Time" type="time" value={at} onChange={(e) => setAt(e.target.value)} />
            <Button
              type="secondary"
              onClick={() => {
                const d = Number(day);
                if (/^\d{2}:\d{2}$/.test(at) && !times.some((x) => x.day === d && x.at === at))
                  setTimes([...times, { day: d, at }]);
              }}
            >
              Add time
            </Button>
          </div>
          <InputText label="Minutes per call" type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
        </Card>
        <Card title="Roles and trial day pay">
          <p className="muted">Trial days are paid by the hour, and the rate depends on the role.</p>
          {roles.map((r, i) => (
            <div key={i} className="row wrap">
              <InputText
                label="Role"
                value={r.name}
                onChange={(e) => setRoles(roles.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
              />
              <InputText
                label="In Spanish"
                value={r.nameEs}
                onChange={(e) => setRoles(roles.map((x, j) => (j === i ? { ...x, nameEs: e.target.value } : x)))}
              />
              <InputText
                label="Trial pay per hour"
                type="number"
                value={r.rate}
                onChange={(e) => setRoles(roles.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x)))}
              />
            </div>
          ))}
          <div>
            <Button
              type="tertiary"
              size="small"
              icon="plus"
              onClick={() => setRoles([...roles, { id: "", name: "", nameEs: "", trialRateCents: 0, rate: "0.00" }])}
            >
              Add role
            </Button>
          </div>
        </Card>
        <Card title="Referral bonus">
          <p className="muted">
            Paid to the crew member who sent someone you hire, after they stay this many days. 0 keeps it off.
          </p>
          <div className="row wrap">
            <InputText label="Bonus" type="number" value={bonus} onChange={(e) => setBonus(e.target.value)} />
            <InputText label="After days" type="number" value={after} onChange={(e) => setAfter(e.target.value)} />
          </div>
        </Card>
      </div>
    </Modal>
  );
}

// Schedule tools --------------------------------------------------------------------------------------------------------

export function ScheduleTools() {
  const { company, can } = useSession();
  const today = todayIn(company.timezone);
  const [date, setDate] = useState(addDays(today, 1));
  const edit = can("schedule.update");
  return (
    <Page title="Time off and capacity">
      <div className="stack">
        <CapacityCard from={today} />
        {edit ? (
          <Card title="Text everyone on a day">
            <InputText label="Day" type="date" value={date} onChange={(e) => setDate(e.target.value || date)} />
            <DayText date={date} />
          </Card>
        ) : null}
        <TimeOffCard from={today} edit={edit} />
      </div>
    </Page>
  );
}

function CapacityCard({ from }: { from: string }) {
  const cap = useCapacity(from, addDays(from, 13));
  if (!cap.data) return <Skeleton rows={2} />;
  if (!cap.data.on)
    return (
      <Card title="Capacity">
        <p className="muted">
          Capacity is off. Turn it on in{" "}
          <Link to="/schedule/settings" hash="capacity">
            Schedule settings
          </Link>{" "}
          to see how full each day is.
        </p>
      </Card>
    );
  return (
    <Card title="Capacity, next two weeks" flush>
      <ul className="auto-list">
        {cap.data.days.map((d) => (
          <li key={d.date} className="setting-row">
            <span className="what">
              <b>{shortDate(d.date)}</b>
              <span>
                {d.teams} {d.teams === 1 ? "team" : "teams"}, {d.people} people, {d.hoursBooked} of {d.hoursHeld} hours booked
              </span>
            </span>
            <StatusLabel status={d.teams === 0 ? "inactive" : d.open ? "success" : "critical"}>
              {d.teams === 0 ? "Closed" : d.open ? "Open" : "Full"}
            </StatusLabel>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function DayText({ date, routeId }: { date: string; routeId?: string }) {
  const send = useDayText();
  const toast = useToast();
  const [message, setMessage] = useState("Hi {first}, a note about your {service} on {day}, {date}. ");
  const [preview, setPreview] = useState<DayTextAnswer | null>(null);
  const body = { date, message, routeId: routeId ?? null, leaveOut: [], skipReminders: true };
  return (
    <div className="stack auto-step">
      <b>Text everyone that day</b>
      <label className="field wide">
        <span className="label">Message. Fill-ins: {"{first}, {service}, {day}, {date}, {address}"}</span>
        <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
      </label>
      {preview ? (
        <div className="stack">
          {preview.samples.map((s) => (
            <pre key={s.to} className="message-preview">
              {s.name}: {s.text}
            </pre>
          ))}
          {preview.skipped.length ? (
            <span className="muted">Skipped: {preview.skipped.map((s) => `${s.name} (${s.why})`).join(", ")}</span>
          ) : null}
        </div>
      ) : null}
      <div className="row">
        <Button
          type="secondary"
          loading={send.isPending}
          onClick={() => send.mutate({ ...body, dry: true }, { onSuccess: setPreview })}
        >
          See who gets it
        </Button>
        {preview ? (
          <Button
            loading={send.isPending}
            onClick={() =>
              send.mutate(
                { ...body, dry: false },
                {
                  onSuccess: (r) => {
                    setPreview(null);
                    toast(`Sent ${String(r.sent)}. Their reminders for that day are off.`);
                  },
                },
              )
            }
          >
            Send
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function TimeOffCard({ from, edit }: { from: string; edit: boolean }) {
  const { company } = useSession();
  const off = useTimeOff(from, addDays(from, 90));
  const team = usePeople(company.id);
  const add = useAddTimeOff();
  const clear = useClearTimeOff();
  const toast = useToast();
  const [draft, setDraft] = useState<{
    personId: string;
    fromDay: string;
    toDay: string;
    whole: "day" | "part";
    fromTime: string;
    toTime: string;
  } | null>(null);
  return (
    <Card
      title="Time off"
      flush
      action={
        edit && !draft ? (
          <Button
            type="secondary"
            size="small"
            icon="plus"
            onClick={() =>
              setDraft({ personId: "", fromDay: from, toDay: from, whole: "day", fromTime: "09:00", toTime: "12:00" })
            }
          >
            Add time off
          </Button>
        ) : null
      }
    >
      {draft ? (
        <div className="stack auto-step">
          <Combobox
            label="Who"
            value={draft.personId}
            onChange={(personId) => setDraft({ ...draft, personId })}
            options={(team.data ?? [])
              .filter((p) => p.active)
              .map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}`.trim() }))}
          />
          <div className="row">
            <InputText
              label="First day"
              type="date"
              value={draft.fromDay}
              onChange={(e) => setDraft({ ...draft, fromDay: e.target.value })}
            />
            <InputText
              label="Last day"
              type="date"
              value={draft.toDay}
              onChange={(e) => setDraft({ ...draft, toDay: e.target.value })}
            />
          </div>
          <Choice
            label="How long"
            value={draft.whole}
            options={[
              { value: "day", label: "Whole day" },
              { value: "part", label: "Part of the day" },
            ]}
            onChange={(whole) => setDraft({ ...draft, whole })}
          />
          {draft.whole === "part" ? (
            <div className="row">
              <InputText
                label="From"
                type="time"
                value={draft.fromTime}
                onChange={(e) => setDraft({ ...draft, fromTime: e.target.value })}
              />
              <InputText
                label="To"
                type="time"
                value={draft.toTime}
                onChange={(e) => setDraft({ ...draft, toTime: e.target.value })}
              />
            </div>
          ) : null}
          <div className="row end">
            <Button type="tertiary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={add.isPending}
              onClick={() =>
                add.mutate(
                  {
                    personId: draft.personId,
                    fromDay: draft.fromDay,
                    toDay: draft.toDay,
                    ...(draft.whole === "part" ? { fromTime: draft.fromTime, toTime: draft.toTime } : {}),
                  },
                  { onSuccess: () => setDraft(null), onError: (e) => toast(errText(e), { kind: "error" }) },
                )
              }
            >
              Save
            </Button>
          </div>
        </div>
      ) : null}
      {!off.data ? (
        <Skeleton rows={2} />
      ) : off.data.timeOff.length === 0 ? (
        <p className="muted auto-intro">Nobody is off in the next 90 days.</p>
      ) : (
        <ul className="auto-list">
          {off.data.timeOff.map((t) => (
            <li key={t.id} className="setting-row">
              <span className="what">
                <b>{t.personName}</b>
                <span>
                  {shortDate(t.fromDay)}
                  {t.toDay !== t.fromDay ? ` to ${shortDate(t.toDay)}` : ""}
                  {t.fromTime ? `, ${t.fromTime} to ${t.toTime ?? ""}` : ", whole day"}
                  {t.reason ? `. ${t.reason}` : ""}
                </span>
              </span>
              {edit ? (
                <Button type="tertiary" size="small" onClick={() => clear.mutate(t.id)}>
                  Clear
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
